import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CalendarPlus, Loader2, Lock, Mail, PencilLine, Unlock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { autoriserReouvertureExercice, creerExerciceAnterieur, fetchExerciceArchives, rouvrirExercice } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const euros = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const dateLongue = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Le courrier de demande d'ouverture, déjà écrit.
 *
 * « Écrivez à l'assistance » laissait chacun composer sa demande — et
 * oublier l'année ou l'adresse de son espace, les deux seules choses dont
 * l'éditeur a besoin pour y répondre. Le lien les porte, il ne reste qu'à
 * dire pourquoi.
 */
function lienDemandeOuverture(annee: number): string {
  const espace = window.location.hostname;
  const objet = `Ouverture de l'exercice ${annee} — ${espace}`;
  const corps = [
    'Bonjour,',
    '',
    `Nous souhaitons compléter l'exercice ${annee} : une pièce nous est revenue après la clôture.`,
    '',
    'Motif (à compléter) :',
    '',
    '',
    `Espace : https://${espace}`,
    `Exercice concerné : ${annee}`,
    '',
    'Merci de le déverrouiller — nous le rouvrirons ensuite depuis nos Archives.',
  ].join('\n');

  return `mailto:assistance@gest-asso.com?subject=${encodeURIComponent(objet)}&body=${encodeURIComponent(corps)}`;
}

/**
 * Exercices clôturés.
 *
 * Une clôture archive l'année sans rien effacer : les chiffres restent
 * consultables, c'est la condition pour qu'on accepte de clôturer.
 */
export default function BureauArchivesPage() {
  const { user, isLoading: authLoading } = useAuth();
  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true || user?.roles?.includes('ROLE_ADMIN') === true;
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['bureau-exercice-archives'],
    queryFn: fetchExerciceArchives,
    enabled: estBureau,
  });

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['bureau-exercice-archives'] });
    // Le tableau de bord compare N à N-1 : un exercice passé qui s'inscrit
    // change ce qu'il a le droit d'afficher.
    queryClient.invalidateQueries({ queryKey: ['bureau-exercice-chiffres'] });
  };

  // Rouvrir mène droit à l'atelier : on ne rouvre pas une année pour rester
  // devant la liste des archives — on la rouvre pour la compléter.
  const rouvrir = useMutation({
    mutationFn: (id: number) => rouvrirExercice(id),
    onSuccess: (_d: any, id: number) => { rafraichir(); navigate(`/bureau/exercices/${id}/completion`); },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  // Refermer ne vit plus ici : le geste appartient à l'atelier de complétion,
  // où l'on voit ce qu'on referme.
  const autoriser = useMutation({
    mutationFn: ({ id, autorisee }: { id: number; autorisee: boolean }) =>
      autoriserReouvertureExercice(id, autorisee),
    onSuccess: (d: any) => { setNotice({ type: 'ok', texte: d.message }); rafraichir(); },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  if (authLoading || (estBureau && isLoading)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!estBureau) {
    return (
      <div className="p-6 lg:p-10 text-center space-y-3 pb-24 lg:pb-0">
        <Lock className="w-12 h-12 mx-auto text-gray-300" />
        <h1 className="text-xl font-bold">Accès réservé</h1>
        <p className="text-gray-600 dark:text-gray-400">Les archives comptables sont réservées au bureau.</p>
      </div>
    );
  }

  const archives = data?.archives ?? [];
  const estSuperAdmin = data?.estSuperAdmin ?? false;
  // Ouvrir et refermer le passé : président, trésorier, exploitant. Le
  // serveur répond, l'écran suit — la vraie garde est chez lui.
  const peutAgir = (data as any)?.peutRouvrir ?? estSuperAdmin;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Archive className="w-6 h-6 text-primary-600" />
            Archives
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Exercices clôturés, du plus récent au plus ancien.
          </p>
          {/* Le circuit, dit en une phrase : sans cela, on cherche un bouton
              qui n'apparaît qu'au deuxième temps, et on croit qu'il manque. */}
          {peutAgir && (
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-2xl">
              Pour compléter une année close — une facture retrouvée après la clôture —
              {estSuperAdmin
                ? ' déverrouillez-la ci-dessous, puis rouvrez-la : '
                : ' demandez son ouverture à l\'assistance ; dès qu\'elle est déverrouillée, le bouton « Rouvrir pour compléter » apparaît ici. '}
              vous disposez alors de {data?.joursReouverture ?? 21} jours de saisie, et l'exercice se
              referme tout seul à l'échéance.
            </p>
          )}
        </div>

        {data?.peutSaisirAnterieur && (
          <SaisieAnterieure
            anneeProposee={data.anneeAnterieureProposee}
            onFait={(message) => { setNotice({ type: 'ok', texte: message }); rafraichir(); }}
            onErreur={(message) => setNotice({ type: 'erreur', texte: message })}
          />
        )}
      </header>

      {notice && (
        <p className={`card text-sm ${notice.type === 'ok' ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {notice.texte}
        </p>
      )}

      {error && <p className="card text-red-600">{(error as Error).message}</p>}

      {archives.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <Archive className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400">
            Aucun exercice clôturé pour l'instant. Le premier apparaîtra ici après la clôture
            de l'année en cours.
          </p>
        </div>
      ) : (
        <>
          {/* Tableau à partir de lg, cartes en dessous. */}
          <div className="hidden lg:block card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Exercice</th>
                  <th className="px-4 py-3 font-medium text-right">Souscrit</th>
                  <th className="px-4 py-3 font-medium text-right">Encaissé</th>
                  <th className="px-4 py-3 font-medium">Clôturé le</th>
                  {peutAgir && <th className="px-4 py-3 font-medium text-right">Écriture</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {archives.map((exercice) => (
                  <tr key={exercice.id}>
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100">
                      {exercice.annee}
                      {exercice.reouvertJusquA && (
                        <span className="ml-2 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-normal text-amber-800 dark:text-amber-300">
                          rouvert jusqu'au {dateLongue.format(new Date(exercice.reouvertJusquA))}
                        </span>
                      )}
                      {!exercice.reouvertJusquA && exercice.reouvertureAutorisee && (
                        <span className="ml-2 rounded-full bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 text-xs font-normal text-primary-800 dark:text-primary-300">
                          déverrouillé
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {euros.format(exercice.souscrit)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-green-600">
                      {euros.format(exercice.chiffre)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {exercice.clotureLe ? dateLongue.format(new Date(exercice.clotureLe)) : '—'}
                    </td>
                    {peutAgir && (
                      <td className="px-4 py-3 text-right">
                        {exercice.reouvertJusquA ? (
                          /* L'atelier plutôt que le geste : c'est là qu'on
                             voit l'échéance, les chiffres et ce qui est déjà
                             entré — refermer s'y trouve aussi. */
                          <Link
                            to={`/bureau/exercices/${exercice.id}/completion`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                            title={`Compléter l'exercice ${exercice.annee}`}
                          >
                            <PencilLine className="w-3.5 h-3.5" /> Compléter
                          </Link>
                        ) : (
                          /* Le circuit en deux temps : l'exploitant
                             déverrouille sur demande écrite, puis le bureau
                             rouvre — jamais l'un sans l'autre. */
                          <span className="inline-flex items-center gap-3 justify-end flex-wrap">
                            {(exercice.reouvertureAutorisee || estSuperAdmin) && (
                              <button
                                type="button"
                                onClick={() => rouvrir.mutate(exercice.id)}
                                disabled={rouvrir.isPending}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 dark:border-primary-700 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
                                title={`Rouvrir ${exercice.annee} pour ${data?.joursReouverture ?? 21} jours de saisie`}
                              >
                                <Unlock className="w-3.5 h-3.5" /> Rouvrir pour compléter
                              </button>
                            )}
                            {estSuperAdmin && (
                              <button
                                type="button"
                                onClick={() => autoriser.mutate({ id: exercice.id, autorisee: !exercice.reouvertureAutorisee })}
                                disabled={autoriser.isPending}
                                className="text-xs text-gray-500 hover:underline disabled:opacity-50"
                                title="Le bureau pourra rouvrir lui-même, une fois"
                              >
                                {exercice.reouvertureAutorisee ? 'Reverrouiller' : 'Déverrouiller pour le bureau'}
                              </button>
                            )}
                            {/* Le lien qui manquait : demander l'ouverture, avec
                                le courrier déjà écrit. « Écrivez à l'assistance »
                                laissait chacun composer sa demande — et oublier
                                l'année ou le nom de l'espace, les deux choses
                                qu'il faut pour y répondre. */}
                            {!estSuperAdmin && !exercice.reouvertureAutorisee && (
                              <a
                                href={lienDemandeOuverture(exercice.annee)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                title={`Demander l'ouverture de ${exercice.annee} à l'assistance`}
                              >
                                <Mail className="w-3.5 h-3.5" /> Demander l'ouverture
                              </a>
                            )}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="lg:hidden space-y-3">
            {archives.map((exercice) => (
              <li key={exercice.id} className="card space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">{exercice.annee}</span>
                  {exercice.reouvertJusquA ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      Rouvert jusqu'au {dateLongue.format(new Date(exercice.reouvertJusquA))}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-800">
                      Clôturé
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Souscrit</span>
                  <span className="tabular-nums">{euros.format(exercice.souscrit)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Encaissé</span>
                  <span className="tabular-nums font-semibold text-green-600">
                    {euros.format(exercice.chiffre)}
                  </span>
                </div>
                {exercice.clotureLe && (
                  <p className="text-xs text-gray-400 pt-1 border-t border-gray-100">
                    Clôturé le {dateLongue.format(new Date(exercice.clotureLe))}
                  </p>
                )}
                {peutAgir && exercice.reouvertJusquA && (
                  <Link
                    to={`/bureau/exercices/${exercice.id}/completion`}
                    className="inline-flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300 hover:underline"
                  >
                    <PencilLine className="w-3.5 h-3.5" /> Compléter l'exercice {exercice.annee}
                  </Link>
                )}
                {peutAgir && !exercice.reouvertJusquA && (exercice.reouvertureAutorisee || estSuperAdmin) && (
                  <button
                    type="button"
                    onClick={() => rouvrir.mutate(exercice.id)}
                    disabled={rouvrir.isPending}
                    className="inline-flex items-center gap-1.5 text-xs text-primary-700 dark:text-primary-300 hover:underline disabled:opacity-50"
                  >
                    <Unlock className="w-3.5 h-3.5" /> Rouvrir {data?.joursReouverture ?? 21} jours
                  </button>
                )}
                {peutAgir && !estSuperAdmin && !exercice.reouvertJusquA && !exercice.reouvertureAutorisee && (
                  <a
                    href={lienDemandeOuverture(exercice.annee)}
                    className="inline-flex items-center gap-1.5 text-xs text-primary-700 dark:text-primary-300 hover:underline"
                  >
                    <Mail className="w-3.5 h-3.5" /> Demander l'ouverture de {exercice.annee}
                  </a>
                )}
                {estSuperAdmin && !exercice.reouvertJusquA && (
                  <button
                    type="button"
                    onClick={() => autoriser.mutate({ id: exercice.id, autorisee: !exercice.reouvertureAutorisee })}
                    disabled={autoriser.isPending}
                    className="block text-xs text-gray-500 hover:underline disabled:opacity-50"
                  >
                    {exercice.reouvertureAutorisee ? 'Reverrouiller' : 'Déverrouiller pour le bureau'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Inscrire une année passée dans les archives.
 *
 * Une association qui arrive sur le logiciel a un passé : elle veut y
 * retrouver 2025, 2024, parfois 2023, ne serait-ce que pour se comparer. Sans
 * cette porte, sa première année ici n'a rien devant elle et le tableau de
 * bord annonce une variation qui ne veut rien dire.
 *
 * L'année naît close — on inscrit le passé, on ne le rouvre pas. Pour y
 * porter des pièces, il faut demander sa réouverture.
 */
function SaisieAnterieure({ anneeProposee, onFait, onErreur }: {
  anneeProposee: number;
  onFait: (message: string) => void;
  onErreur: (message: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [annee, setAnnee] = useState(String(anneeProposee));

  const creer = useMutation({
    mutationFn: () => creerExerciceAnterieur(parseInt(annee, 10)),
    onSuccess: (d: any) => {
      onFait(d.message);
      setOuvert(false);
      // L'année d'après, on remonte encore d'un cran : c'est de proche en
      // proche qu'une association reprend son passé.
      setAnnee(String(parseInt(annee, 10) - 1));
    },
    onError: (e: Error) => onErreur(e.message),
  });

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="btn-outline inline-flex items-center gap-2 text-sm whitespace-nowrap"
      >
        <CalendarPlus className="w-4 h-4" /> Ajouter un exercice passé
      </button>
    );
  }

  return (
    <div className="card p-3 space-y-2 w-full sm:w-auto">
      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
        L'année s'inscrit close, dans les archives. Pour y porter des pièces ensuite, le bouton
        « Demander l'ouverture » apparaîtra sur sa ligne : une fois déverrouillée, vous la rouvrez
        d'un clic pour vingt-et-un jours de saisie.
      </p>
      <div className="flex items-center gap-2">
        <input
          className="input w-24 tabular-nums"
          inputMode="numeric"
          value={annee}
          onChange={(e) => setAnnee(e.target.value.replace(/\D/g, '').slice(0, 4))}
          onKeyDown={(e) => { if (e.key === 'Enter') creer.mutate(); }}
        />
        <button
          type="button"
          onClick={() => creer.mutate()}
          disabled={creer.isPending || annee.length !== 4}
          className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50"
        >
          {creer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
        </button>
        <button
          type="button"
          onClick={() => setOuvert(false)}
          className="text-sm text-gray-500 hover:text-gray-700 px-2"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}
